import { Controller } from '@hotwired/stimulus';
import { styles, getStyle } from '@yobbers/js/utils/dom';
import { gsap } from 'gsap';

const options = {
  ACTIVE_CLASS: '-is-active',
  DISABLED_CLASS: '-is-disabled',
  DURATION: 0.2,
  OPEN_STATE: 'open',
  CLOSED_STATE: 'closed',
  ARIA_EXPANDED: 'aria-expanded'
};

export default class extends Controller {
  static targets = ['body'];
  static values = {
    saveStateId: String
  };

  initialize() {
    this.updateBodyDisplay();
    this.updateOpenState();
  }

  toggle(event) {
    if (this.isActive()) {
      this.updateAriaTag(event, options.CLOSED_STATE);
      this.close();
      return;
    }
    this.updateAriaTag(event, options.OPEN_STATE);
    this.open();
  }

  open(instant = false) {
    this.addActiveClass();
    this._animateIn(instant);
    this.updateSessionStorage(options.OPEN_STATE);
  }

  close(instant = false) {
    this.removeActiveClass();
    this._animateOut(instant);
    this.updateSessionStorage(options.CLOSED_STATE);
  }

  updateAriaTag(event, state) {
    const target = this.bodyTarget;
    if (!target.hasAttribute(options.ARIA_EXPANDED)) return;
    target.setAttribute(options.ARIA_EXPANDED, state);
  }

  updateBodyDisplay() {
    if (this.isActive()) {
      this.bodyTarget.style.display = 'block';
    }
  }

  updateOpenState() {
    if (this.saveStateIdValue && !this.isDisabled()) {
      const state = sessionStorage.getItem(this.saveStateIdValue);
      if (state === options.OPEN_STATE) this.open(true);
      if (state === options.CLOSED_STATE) this.close(true);
    }
  }

  updateSessionStorage(state) {
    if (this.saveStateIdValue) {
      sessionStorage.setItem(this.saveStateIdValue, state);
    }
  }

  isActive() {
    return this.element.classList.contains(options.ACTIVE_CLASS);
  }

  isDisabled() {
    return this.element.classList.contains(options.DISABLED_CLASS);
  }

  addActiveClass() {
    this.element.classList.add(options.ACTIVE_CLASS);
  }

  removeActiveClass() {
    this.element.classList.remove(options.ACTIVE_CLASS);
  }

  _animateIn(instant) {
    gsap.killTweensOf(this.bodyTarget);

    styles(this.bodyTarget, {
      display: 'block',
      overflow: 'hidden',
      height: 0,
      paddingTop: '',
      paddingBottom: ''
    });

    const pTop = getStyle(this.bodyTarget, 'padding-top');
    const pBottom = getStyle(this.bodyTarget, 'padding-bottom');
    const finalHeight = this.bodyTarget.scrollHeight;

    styles(this.bodyTarget, {
      paddingTop: 0,
      paddingBottom: 0
    });

    gsap.to(this.bodyTarget, {
      height: finalHeight,
      paddingTop: pTop,
      paddingBottom: pBottom,
      duration: instant ? 0 : options.DURATION + 0.05,
      opacity: [0, 1],
      ease: 'back.out(1.5)',
      onComplete: () => {
        styles(this.bodyTarget, {
          overflow: '',
          paddingTop: '',
          paddingBottom: '',
          height: ''
        });
      }
    });
  }

  _animateOut(instant) {
    gsap.killTweensOf(this.bodyTarget);

    this.bodyTarget.style.overflow = 'hidden';

    gsap.to(this.bodyTarget, {
      height: 0,
      paddingTop: 0,
      paddingBottom: 0,
      opacity: [1, 0],
      duration: instant ? 0 : options.DURATION,
      ease: 'power4.out',
      onComplete: (anim) => {
        styles(this.bodyTarget, {
          height: '',
          overflow: '',
          padding: '',
          display: 'none'
        });
      }
    });
  }
}
